// Active-filter banner for the SOW section. Mirrors MeetingActiveFilterSummary:
// renders a single-line prose summary of every active chip + search term and a
// Clear all button. Returns null when no filter or search is active so the
// surface stays calm in the default state.

import type { SowStatus } from "../../../../types";
import type { SowDateRangeFilter, SowFilters } from "./applySowFilters";
import { hasAnyActiveSowFilter } from "./hasAnyActiveSowFilter";

export interface SowActiveFilterSummaryProps {
  filters: SowFilters;
  search: string;
  onClearAll: () => void;
}

const STATUS_LABEL: Record<SowStatus, string> = {
  draft: "Draft",
  active: "Active",
  completed: "Completed",
  cancelled: "Cancelled",
};

const DATE_RANGE_LABEL: Record<SowDateRangeFilter, string> = {
  "next-30-days": "Next 30 days",
  "past-30-days": "Past 30 days",
  "this-quarter": "This quarter",
  "all-time": "All time",
};

function joinLabels<T extends string>(
  values: T[],
  labels: Record<T, string>,
): string {
  return values.map((v) => labels[v]).join(", ");
}

export default function SowActiveFilterSummary({
  filters,
  search,
  onClearAll,
}: SowActiveFilterSummaryProps) {
  if (!hasAnyActiveSowFilter(filters, search)) return null;

  const parts: string[] = [];
  if (filters.status.length > 0) {
    parts.push(`Status: ${joinLabels(filters.status, STATUS_LABEL)}`);
  }
  if (filters.dateRange !== "all-time") {
    parts.push(`Date range: ${DATE_RANGE_LABEL[filters.dateRange]}`);
  }
  if (filters.includeArchived) {
    parts.push("Include archived");
  }
  const trimmedSearch = search.trim();
  if (trimmedSearch) {
    parts.push(`Search: '${trimmedSearch}'`);
  }

  return (
    <div
      data-testid="sow-active-filter-summary"
      className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-caption text-slate-600 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-300"
    >
      <span>{parts.join(" · ")}</span>
      <button
        type="button"
        onClick={onClearAll}
        data-testid="sow-active-filter-clear-all"
        className="text-caption font-medium text-brand-600 hover:underline focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-brand-400"
      >
        Clear all
      </button>
    </div>
  );
}
